import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import api from "@/lib/api";
import { toast } from "sonner";

const BusinessEditDialog = ({ open, onClose, onSaved, categories = [], existing = null }) => {
  const [name, setName] = useState("");
  const [category, setCategory] = useState("other");
  const [description, setDescription] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [coverImage, setCoverImage] = useState("");
  const [website, setWebsite] = useState("");
  const [instagram, setInstagram] = useState("");
  const [twitter, setTwitter] = useState("");
  const [saving, setSaving] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);
  const coverInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (existing) {
      setName(existing.name || "");
      setCategory(existing.category || "other");
      setDescription(existing.description || "");
      setContactEmail(existing.contact_email || "");
      setContactPhone(existing.contact_phone || "");
      setCoverImage(existing.cover_image || "");
      const links = existing.links || {};
      setWebsite(links.website || "");
      setInstagram(links.instagram || "");
      setTwitter(links.twitter || "");
    } else {
      setName(""); setCategory("other"); setDescription("");
      setContactEmail(""); setContactPhone(""); setCoverImage("");
      setWebsite(""); setInstagram(""); setTwitter("");
    }
  }, [open, existing]);

  const submit = async () => {
    if (name.trim().length < 2) {
      toast.error("Name is required");
      return;
    }
    setSaving(true);
    try {
      // For NEW businesses, if coverImage is a data: URL (local preview), we
      // need to upload it AFTER create via the /cover endpoint. Don't send the
      // base64 blob to the create endpoint.
      const isDataUrl = coverImage?.startsWith("data:");
      const body = {
        name: name.trim(),
        category,
        description: description.trim(),
        contact_email: contactEmail.trim() || null,
        contact_phone: contactPhone.trim() || null,
        cover_image: isDataUrl ? null : (coverImage.trim() || null),
        links: {
          website: website.trim() || null,
          instagram: instagram.trim() || null,
          twitter: twitter.trim() || null,
        },
      };
      let res;
      if (existing) {
        res = await api.patch(`/businesses/${existing.business_id}`, body);
      } else {
        res = await api.post("/businesses", body);
        // If the user selected a file to upload, upload it now and merge the URL
        if (isDataUrl && coverInputRef.current?.files?.[0]) {
          try {
            const fd = new FormData();
            fd.append("image", coverInputRef.current.files[0]);
            const r2 = await api.post(
              `/businesses/${res.data.business_id}/cover`,
              fd,
              { headers: { "Content-Type": "multipart/form-data" } }
            );
            res.data.cover_image = r2.data?.cover_image || res.data.cover_image;
          } catch (err) {
            toast.error(err.response?.data?.detail || "Cover upload failed — you can retry from the profile");
          }
        }
      }
      onSaved && onSaved(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save business");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onClose}
      data-testid="business-edit-dialog"
    >
      <div
        className="w-full max-w-lg bg-[#161620] border border-purple-500/40 rounded-xl p-6 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-white">
            {existing ? "Edit business" : "List your business"}
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-2xl" aria-label="Close">×</button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-400">Business name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="bg-black/40 text-white border-gray-700" data-testid="business-name-input" />
          </div>
          <div>
            <label className="text-xs text-gray-400">Category</label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="w-full text-sm px-3 py-2 rounded-md bg-black/40 text-white border border-gray-700"
              data-testid="business-category-select"
            >
              {categories.map((c) => (
                <option key={c.key} value={c.key}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs text-gray-400">Description</label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="bg-black/40 text-white border-gray-700" data-testid="business-description-input" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-gray-400">Contact email</label>
              <Input value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} className="bg-black/40 text-white border-gray-700" data-testid="business-email-input" />
            </div>
            <div>
              <label className="text-xs text-gray-400">Contact phone</label>
              <Input value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} className="bg-black/40 text-white border-gray-700" data-testid="business-phone-input" />
            </div>
          </div>
          <div>
            <label className="text-xs text-gray-400 block mb-1">Cover image</label>
            <div className="flex flex-col sm:flex-row gap-2 items-start">
              {coverImage && (
                <div className="w-24 h-16 rounded-md overflow-hidden bg-black/40 border border-gray-700 shrink-0">
                  <img src={coverImage} alt="Cover preview" className="w-full h-full object-cover" />
                </div>
              )}
              <div className="flex gap-2 flex-wrap">
                <input
                  ref={coverInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/webp,image/gif"
                  className="hidden"
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (f.size > 10 * 1024 * 1024) { toast.error("Max 10 MB"); return; }
                    // When editing an existing business, upload directly and get CDN URL
                    if (existing?.business_id) {
                      setUploadingCover(true);
                      try {
                        const fd = new FormData();
                        fd.append("image", f);
                        const r = await api.post(`/businesses/${existing.business_id}/cover`, fd, {
                          headers: { "Content-Type": "multipart/form-data" },
                        });
                        setCoverImage(r.data?.cover_image || "");
                        toast.success("Cover uploaded");
                      } catch (err) {
                        toast.error(err.response?.data?.detail || "Upload failed");
                      } finally {
                        setUploadingCover(false);
                        if (coverInputRef.current) coverInputRef.current.value = "";
                      }
                    } else {
                      // New business: use local preview now; we'll upload cover after create
                      const reader = new FileReader();
                      reader.onload = () => setCoverImage(reader.result);
                      reader.readAsDataURL(f);
                      toast.message("Cover will upload after you save the business");
                    }
                  }}
                  data-testid="business-cover-file"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => coverInputRef.current?.click()}
                  disabled={uploadingCover}
                  className="text-white border-gray-700"
                  data-testid="business-cover-upload-button"
                >
                  {uploadingCover ? "Uploading…" : coverImage ? "Change cover" : "Upload cover"}
                </Button>
                {coverImage && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setCoverImage("")}
                    className="text-red-300 border-red-500/40"
                  >
                    Remove
                  </Button>
                )}
              </div>
            </div>
            <p className="text-[11px] text-gray-500 mt-1">JPG, PNG, WebP, or GIF — up to 10 MB.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-gray-400">Website</label>
              <Input value={website} onChange={(e) => setWebsite(e.target.value)} className="bg-black/40 text-white border-gray-700" />
            </div>
            <div>
              <label className="text-xs text-gray-400">Instagram</label>
              <Input value={instagram} onChange={(e) => setInstagram(e.target.value)} className="bg-black/40 text-white border-gray-700" />
            </div>
            <div>
              <label className="text-xs text-gray-400">X / Twitter</label>
              <Input value={twitter} onChange={(e) => setTwitter(e.target.value)} className="bg-black/40 text-white border-gray-700" />
            </div>
          </div>
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <Button onClick={onClose} variant="outline" className="text-white border-gray-700">Cancel</Button>
          <Button onClick={submit} disabled={saving} className="bg-purple-600 hover:bg-purple-500 text-white" data-testid="business-save-button">
            {saving ? "Saving…" : existing ? "Save" : "Create"}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default BusinessEditDialog;
